import React from 'react';
import { useTheme } from './themeControl/ThemeProvider';
import defaultLogo from './img/LogiQOS_logo.png';
import defaultLogoDark from './img/LogiQOS_logo-dark.png';
import './css/Styles.css';
import './css/GlobalStyles.css';

const PrivacyPolicy = () => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const logoSrc = isDark ? defaultLogoDark : defaultLogo;

    const s = {
        page: {
            position: 'fixed',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        },
        header: {
            position: 'relative',
            zIndex: 2,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            padding: '16px 40px',
            background: isDark ? 'rgba(10,10,10,0.88)' : 'rgba(255,255,255,0.88)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)'}`,
        },
        logo: { height: '42px' },
        headerLabel: {
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '1px',
            textTransform: 'uppercase',
            color: '#1997eb',
            marginBottom: '2px',
        },
        headerTagline: {
            fontSize: '13px',
            color: isDark ? '#666' : '#6b7280',
        },
        scrollArea: {
            position: 'relative',
            zIndex: 1,
            flex: 1,
            overflowY: 'auto',
            padding: '36px 24px 60px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
        },
        panel: {
            width: '100%',
            maxWidth: '860px',
            background: isDark ? 'rgba(10,10,10,0.75)' : 'rgba(255,255,255,0.80)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.09)'}`,
            borderRadius: '14px',
            padding: '36px 40px 44px',
        },
        h1: {
            fontSize: '28px',
            fontWeight: 700,
            color: isDark ? '#ffffff' : '#0a0a0a',
            marginBottom: '6px',
        },
        meta: {
            fontSize: '13px',
            color: isDark ? '#555' : '#9ca3af',
            marginBottom: '28px',
        },
        h2: {
            fontSize: '16px',
            fontWeight: 600,
            color: isDark ? '#ffffff' : '#0a0a0a',
            marginTop: '32px',
            marginBottom: '10px',
            borderLeft: '4px solid #1997eb',
            paddingLeft: '12px',
        },
        infoCard: {
            background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)'}`,
            borderRadius: '10px',
            padding: '18px 22px',
            marginTop: '8px',
            marginBottom: '8px',
        },
        p: {
            fontSize: '15px',
            lineHeight: '1.75',
            color: isDark ? '#a5a4a4' : '#374151',
            margin: '0 0 10px',
        },
        ul: { marginLeft: '22px', marginTop: '8px', marginBottom: '10px' },
        li: {
            fontSize: '15px',
            lineHeight: '1.75',
            color: isDark ? '#a5a4a4' : '#374151',
            marginBottom: '6px',
        },
        strong: { color: isDark ? '#d1d1d1' : '#111827' },
        a: { color: '#1997eb', textDecoration: 'none' },
        footer: {
            textAlign: 'center',
            paddingTop: '28px',
            marginTop: '36px',
            fontSize: '13px',
            color: isDark ? '#444' : '#9ca3af',
            borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)'}`,
        },
        footerLogo: {
            height: '26px',
            opacity: 0.4,
            display: 'block',
            margin: '0 auto 8px',
        },
    };

    return (
        <div style={s.page}>
            <div className={isDark ? 'dark-fullscreen-background' : 'fullscreen-background'} />

            <div style={s.header}>
                <img src={logoSrc} alt="LogiqOS" style={s.logo} />
                <div>
                    <div style={s.headerLabel}>Mobile Application</div>
                    <div style={s.headerTagline}>Fleet &amp; Logistics Management Platform</div>
                </div>
            </div>

            <div style={s.scrollArea}>
                <div style={s.panel}>

                    <h1 style={s.h1}>Privacy Policy</h1>
                    <p style={s.meta}>Last updated: 13 July 2026 &nbsp;|&nbsp; Effective date: 13 July 2026</p>

                    <div style={s.infoCard}>
                        <p style={s.p}>
                            This Privacy Policy explains how <strong style={s.strong}>LogiqOS</strong> (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) collects,
                            uses, stores, and protects information when you use the LogiqOS mobile application (the &quot;App&quot;).
                            By using the App you agree to the practices described in this policy.
                        </p>
                    </div>

                    <h2 style={s.h2}>1. Who We Are</h2>
                    <p style={s.p}>
                        LogiqOS is a fleet and logistics management platform developed and operated by Hollies Coding.
                        The App is provided to transport and logistics businesses to help manage drivers, vehicles,
                        routes, deliveries, and compliance operations.
                    </p>

                    <h2 style={s.h2}>2. Information We Collect</h2>
                    <p style={s.p}>We collect the following categories of information when you use the App:</p>
                    <ul style={s.ul}>
                        <li style={s.li}><strong style={s.strong}>Account &amp; Profile Data:</strong> Your name, surname, email address, job role, and employer details provided during registration or maintained by your administrator.</li>
                        <li style={s.li}><strong style={s.strong}>Location Data:</strong> Precise GPS location is collected while you are actively performing a route or delivery, to enable live tracking, route completion recording, and GPS-stamped delivery confirmations. Location is <em>not</em> collected in the background when the App is closed.</li>
                        <li style={s.li}><strong style={s.strong}>Camera &amp; Photos:</strong> The App requests camera access to capture vehicle defect photos, delivery proof-of-delivery images, alcohol test evidence, tachograph paperwork scans, and vehicle documentation. Images are uploaded to secure cloud storage and are not processed for facial recognition or any unrelated purpose.</li>
                        <li style={s.li}><strong style={s.strong}>Device &amp; Usage Data:</strong> Device type, operating system version, app version, crash reports, and anonymised usage events used solely for stability and quality improvement.</li>
                        <li style={s.li}><strong style={s.strong}>Work &amp; Operational Data:</strong> Timesheets, holiday requests, vehicle checks, defect reports, delivery records, and other data entered by you in the course of your employment.</li>
                        <li style={s.li}><strong style={s.strong}>Push Notification Tokens:</strong> A Firebase Cloud Messaging (FCM) token is stored to deliver operational notifications (e.g., route assignments, holiday approvals). This token is tied to your device and is not shared with third parties for marketing.</li>
                    </ul>

                    <h2 style={s.h2}>3. How We Use Your Information</h2>
                    <ul style={s.ul}>
                        <li style={s.li}>To provide and operate the App&apos;s core features (routing, vehicle management, timesheets, deliveries).</li>
                        <li style={s.li}>To enable your employer to monitor fleet operations, compliance, and driver activity as required by their legal obligations.</li>
                        <li style={s.li}>To send you operational push notifications relevant to your role.</li>
                        <li style={s.li}>To diagnose and fix technical issues, crashes, and bugs.</li>
                        <li style={s.li}>To comply with applicable laws and regulations (e.g., road transport regulations, tachograph rules).</li>
                    </ul>
                    <p style={s.p}>We <strong style={s.strong}>do not</strong> sell, rent, or share your personal data with third parties for marketing or advertising purposes.</p>

                    <h2 style={s.h2}>4. Legal Basis for Processing</h2>
                    <p style={s.p}>Where applicable, we rely on the following legal bases under UK/EU data protection law:</p>
                    <ul style={s.ul}>
                        <li style={s.li}><strong style={s.strong}>Contract:</strong> Processing necessary to provide the services your employer has contracted.</li>
                        <li style={s.li}><strong style={s.strong}>Legitimate interests:</strong> Operational monitoring, safety, and fleet compliance.</li>
                        <li style={s.li}><strong style={s.strong}>Legal obligation:</strong> Retention of driving and compliance records as required by transport law.</li>
                        <li style={s.li}><strong style={s.strong}>Consent:</strong> For optional features such as push notifications (you may withdraw consent in your device settings at any time).</li>
                    </ul>

                    <h2 style={s.h2}>5. Data Sharing</h2>
                    <p style={s.p}>Your data is shared only with the following parties and only as necessary:</p>
                    <ul style={s.ul}>
                        <li style={s.li}><strong style={s.strong}>Your employer / fleet operator:</strong> As the data controller, your employer accesses operational records through the LogiqOS platform.</li>
                        <li style={s.li}><strong style={s.strong}>Supabase:</strong> Secure cloud database and file storage provider. Data is stored in European (EU) data centres and subject to Supabase&apos;s data processing agreement.</li>
                        <li style={s.li}><strong style={s.strong}>Google Firebase:</strong> Used for push notification delivery (FCM). Firebase processes device tokens in accordance with Google&apos;s privacy policy.</li>
                        <li style={s.li}><strong style={s.strong}>Google Maps:</strong> Used to display route and delivery maps within the App. Map interactions are subject to Google&apos;s privacy policy.</li>
                        <li style={s.li}><strong style={s.strong}>Heroku (Salesforce):</strong> Cloud hosting infrastructure on which the LogiqOS backend runs.</li>
                    </ul>

                    <h2 style={s.h2}>6. Data Retention</h2>
                    <p style={s.p}>
                        We retain personal and operational data for as long as required by your employer&apos;s account and applicable legal obligations.
                        Tachograph and driving records may be retained for up to <strong style={s.strong}>2 years</strong> to comply with transport regulations.
                        When an employer account is closed, data is deleted or anonymised within 90 days unless retention is required by law.
                    </p>

                    <h2 style={s.h2}>7. Your Rights</h2>
                    <p style={s.p}>Depending on your location, you may have the following rights:</p>
                    <ul style={s.ul}>
                        <li style={s.li}>Access the personal data we hold about you.</li>
                        <li style={s.li}>Request correction of inaccurate data.</li>
                        <li style={s.li}>Request deletion of your data (subject to legal retention requirements).</li>
                        <li style={s.li}>Object to or restrict certain processing.</li>
                        <li style={s.li}>Data portability (receive your data in a structured, machine-readable format).</li>
                        <li style={s.li}>Withdraw consent for optional processing at any time.</li>
                    </ul>
                    <p style={s.p}>To exercise any of these rights, please contact us at the address below. We will respond within 30 days.</p>

                    <h2 style={s.h2}>8. Security</h2>
                    <p style={s.p}>We implement industry-standard security measures including:</p>
                    <ul style={s.ul}>
                        <li style={s.li}>TLS encryption for all data in transit.</li>
                        <li style={s.li}>AES-256 encryption for data at rest in Supabase storage.</li>
                        <li style={s.li}>Session-based authentication with secure, HttpOnly cookies.</li>
                        <li style={s.li}>Rate limiting and security event monitoring.</li>
                        <li style={s.li}>Row-level security policies on all database tables.</li>
                    </ul>
                    <p style={s.p}>While we take every reasonable precaution, no system is completely immune to attack. If you suspect a security issue, please report it to us immediately.</p>

                    <h2 style={s.h2}>9. Children&apos;s Privacy</h2>
                    <p style={s.p}>
                        The App is intended for use by employed adults only. We do not knowingly collect personal data from anyone under the age of 16.
                        If you believe a minor has used the App, please contact us so we can take appropriate action.
                    </p>

                    <h2 style={s.h2}>10. Changes to This Policy</h2>
                    <p style={s.p}>
                        We may update this Privacy Policy from time to time. When we do, we will revise the &quot;Last updated&quot; date at the top of this page.
                        For significant changes, we will notify you via the App or by email. Continued use of the App after changes are posted
                        constitutes acceptance of the updated policy.
                    </p>

                    <h2 style={s.h2}>11. Contact Us</h2>
                    <p style={s.p}>If you have any questions about this Privacy Policy or wish to exercise your data rights, please contact:</p>
                    <div style={s.infoCard}>
                        <p style={{ ...s.p, marginBottom: 0 }}>
                            <strong style={s.strong}>LogiqOS &ndash; Data Privacy</strong><br />
                            Developed by Hollies Coding<br />
                            Email: <a href="mailto:holliescoding@gmail.com" style={s.a}>holliescoding@gmail.com</a>
                        </p>
                    </div>

                    <div style={s.footer}>
                        <img src={logoSrc} alt="LogiqOS" style={s.footerLogo} />
                        &copy; 2026 LogiqOS by Hollies Coding. All rights reserved.
                    </div>

                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
